import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, exhaustMap, map, of, switchMap, tap } from "rxjs";

import { NotificationService } from "../../_services/notification.service";
import * as OwnerFeatureFlagActions from "./owner-feature-flags.actions";
import { OwnerFeatureFlagsService } from "./owner-feature-flags.service";

@Injectable()
export class OwnerFeatureFlagsEffects {
  private actions$ = inject(Actions);
  private service = inject(OwnerFeatureFlagsService);
  private notificationService = inject(NotificationService);

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OwnerFeatureFlagActions.loadAllProductsFeatures),
      switchMap(() =>
        this.service.getAll().pipe(
          map(summaries =>
            OwnerFeatureFlagActions.loadAllProductsFeaturesSuccess({
              summaries,
            })
          ),
          catchError(error =>
            of(
              OwnerFeatureFlagActions.loadAllProductsFeaturesFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );

  // exhaustMap drops duplicate dispatches while a save is in-flight, preventing
  // concurrent POST requests and race conditions on bulkSaving store state.
  bulk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OwnerFeatureFlagActions.bulkSetProductFeatures),
      exhaustMap(({ update }) =>
        this.service.bulk(update).pipe(
          map(() =>
            OwnerFeatureFlagActions.bulkSetProductFeaturesSuccess({ update })
          ),
          catchError(error =>
            of(
              OwnerFeatureFlagActions.bulkSetProductFeaturesFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );

  bulkSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OwnerFeatureFlagActions.bulkSetProductFeaturesSuccess),
        tap(() =>
          this.notificationService.showSuccess(
            "Feature flags updated successfully."
          )
        )
      ),
    { dispatch: false }
  );

  failure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          OwnerFeatureFlagActions.loadAllProductsFeaturesFailure,
          OwnerFeatureFlagActions.bulkSetProductFeaturesFailure
        ),
        tap(action => {
          const message =
            action.type ===
            OwnerFeatureFlagActions.bulkSetProductFeaturesFailure.type
              ? "Failed to update feature flags. Please try again."
              : "Failed to load feature flags. Please try again.";
          this.notificationService.showError(message);
        })
      ),
    { dispatch: false }
  );
}
